import { motion } from "framer-motion";

/**
 * Elegant nodal network — coloured nodes linked by hairlines.
 * A travelling pulse glides along each connection, suggesting flow.
 * Used as the hero side accent.
 */

const NODES = [
  { id: "a", x: 90, y: 40,  colour: "#E63329", label: "Structure" },
  { id: "b", x: 30, y: 110, colour: "#C8941F", label: "Communication" },
  { id: "c", x: 200, y: 130, colour: "#146C73", label: "Workflow" },
  { id: "d", x: 130, y: 230, colour: "#0F1115", label: "Admin" },
  { id: "e", x: 260, y: 260, colour: "#8B5E3C", label: "Day-to-day" },
];

const EDGES = [
  { from: "a", to: "b", delay: 0.2 },
  { from: "a", to: "c", delay: 0.4 },
  { from: "b", to: "d", delay: 0.6 },
  { from: "c", to: "d", delay: 0.8 },
  { from: "c", to: "e", delay: 1.0 },
  { from: "d", to: "e", delay: 1.2 },
];

function nodeById(id) {
  return NODES.find((n) => n.id === id);
}

export default function HeroAccent({ className = "" }) {
  return (
    <div className={`relative ${className}`} aria-hidden>
      <div className="relative aspect-square w-full max-w-[460px] ml-auto">
        <svg
          viewBox="0 0 320 320"
          className="w-full h-full"
          fill="none"
        >
          {/* Edges — animated draw, then a sliding pulse */}
          {EDGES.map((e, i) => {
            const from = nodeById(e.from);
            const to = nodeById(e.to);
            const pathId = `edge-${i}`;
            return (
              <g key={pathId}>
                <motion.line
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="#0F1115"
                  strokeOpacity="0.18"
                  strokeWidth="1"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: 1.1,
                    delay: 0.6 + e.delay,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                />
              </g>
            );
          })}

          {/* Edge pulses — small travelling circles */}
          {EDGES.map((e, i) => {
            const from = nodeById(e.from);
            const to = nodeById(e.to);
            return (
              <motion.circle
                key={`pulse-${i}`}
                r="2.5"
                fill={from.colour}
                initial={{ cx: from.x, cy: from.y, opacity: 0 }}
                animate={{
                  cx: [from.x, to.x, from.x],
                  cy: [from.y, to.y, from.y],
                  opacity: [0, 0.95, 0.95, 0],
                }}
                transition={{
                  duration: 4 + i * 0.6,
                  delay: 1.6 + i * 0.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  times: [0, 0.45, 0.55, 1],
                }}
              />
            );
          })}

          {/* Nodes */}
          {NODES.map((n, i) => (
            <g key={n.id}>
              {/* Soft halo for the primary brand-red node */}
              {n.colour === "#E63329" && (
                <motion.circle
                  cx={n.x}
                  cy={n.y}
                  r="18"
                  fill={n.colour}
                  fillOpacity="0.12"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [1, 1.25, 1], opacity: [0.12, 0.05, 0.12] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    delay: 1.5,
                    ease: "easeInOut",
                  }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                />
              )}
              <motion.circle
                cx={n.x}
                cy={n.y}
                r="6"
                fill={n.colour}
                stroke="white"
                strokeWidth="2"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.5 + i * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ transformOrigin: `${n.x}px ${n.y}px` }}
              />
              {/* Label */}
              <motion.text
                x={n.x + 12}
                y={n.y + 4}
                fontFamily="Manrope, sans-serif"
                fontSize="9"
                fontWeight="600"
                letterSpacing="0.18em"
                fill="#0F1115"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.85 }}
                transition={{ duration: 0.5, delay: 1.0 + i * 0.1 }}
              >
                {n.label.toUpperCase()}
              </motion.text>
            </g>
          ))}

          {/* Decorative crosshair in lower-left */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.35 }}
            transition={{ duration: 0.8, delay: 2.0 }}
            stroke="#0F1115"
            strokeWidth="1"
          >
            <line x1="20" y1="290" x2="40" y2="290" />
            <line x1="30" y1="280" x2="30" y2="300" />
          </motion.g>
        </svg>

        {/* Small annotation overlay */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 2.2 }}
          className="absolute bottom-2 right-2 text-right"
        >
          <div className="overline text-ink-soft text-[10px]">Where pieces</div>
          <div className="font-serif-display italic text-ink text-lg leading-tight">
            connect
          </div>
        </motion.div>
      </div>
    </div>
  );
}
